import React, {
	useState,
	useEffect,
	createContext,
	useCallback,
	useContext,
} from "react";
import {
	placeIdField,
	placeSearchField,
	urbanIdField,
	placeServiceLayer,
	urbanAreaLayer,
	parkServePlaceLayerId,
	parkServeUrbanLayerId,
	priorityAreaPlaceFLId,
	parkLayerId,
	parkServiceLayerId,
	prioritizeLayerId,
	dataFields
} from "../constants";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { MapViewContext } from "./MapViewContext";
import { useSearchParams } from "react-router-dom";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import { CompareContext } from "./CompareContext";
import Graphic from "@arcgis/core/Graphic";
import { trackEvent } from "../hooks/useAnalytics";

type City = {
	name: string;
	id: string;
	geometry: __esri.Geometry;
	attributes: any;
	feature: any;
};

const CityContext = createContext<
	Partial<{
		city: City;
		//setCity: (objectId: number) => void;
		setCity: (id: string) => void;
		urban: Graphic;
		setUrban: (urbanID: number) => void;
		isInitialLoad: Boolean;
		setInitialLoad: (tf: boolean) => void;
	}>
>({});
export default CityContext;

export function CityProvider(props: any) {
	const { compare } = useContext(CompareContext);
	const [city, setCity] = useState<City>();
	const [urban, setUrban] = useState<any>();
	const view = useContext(MapViewContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitialLoad, setInitialLoad] = useState<boolean>(true);

	let parkServeUrbanLayer: FeatureLayer;
	let parkServePlaceLayer: FeatureLayer;
	let priorityAreaPlaceFL: FeatureLayer;

	useEffect(() => {
		//console.log(isInitialLoad, city?.name, searchParams);
		if (isInitialLoad && city) {
			//console.log(isInitialLoad, city.name);
			//console.log("first load");
			trackEvent("city_page", city.name);
			setInitialLoad(false);
			//@ts-ignore
		} else if (isInitialLoad && searchParams?.size == 0) {
			//console.log(isInitialLoad, "url params is 0");
			setInitialLoad(false);
		}

	}, [city, isInitialLoad, searchParams]);

	useEffect(() => {
		if (compare != city?.attributes[placeIdField]) {
			getCity(compare);
		}
	}, [compare]);

	useEffect(() => {
		view?.when((mV: __esri.MapView) => {
			searchParams.forEach((sp: any) => {
				//console.log(sp);
				sp && getCity?.(sp);
			});
		});
	}, [view]);

	// This function selects a city or urban area based on the user's search terms.
	const getCity = useCallback(
		async (id: string) => {
			const { features } = await placeServiceLayer.queryFeatures({
				where: `${placeIdField} = '${id}'`,
				outFields: ["*"],
				returnGeometry: true,
				outSpatialReference: {
					wkid: 102100,
				},
			});
			if (features.length) {
				const feature = features[0];
				const { attributes, geometry } = feature;
				getUrban(attributes[urbanIdField]);
				view?.when((mv: __esri.MapView) => {
					mv.goTo(feature);
				});

				const id = attributes[placeIdField];
				const name = attributes[placeSearchField];

				setCity({
					name,
					id,
					geometry,
					attributes,
					feature,
				});

				setSearchParams({ CityID: id });

				if (view) {
					//@ts-ignore
					parkServePlaceLayer = view.map.findLayerById(parkServePlaceLayerId);
					//@ts-ignore
					parkServeUrbanLayer = view.map.findLayerById(parkServeUrbanLayerId);
					//@ts-ignore
					priorityAreaPlaceFL = view.map.findLayerById(priorityAreaPlaceFLId);
					let parkLayer = view.map.findLayerById(parkLayerId);
					//@ts-ignore
					let parkServiceLayer: __esri.FeatureLayer =
						view.map.findLayerById(parkServiceLayerId);
					let prioritizeFL = view.map.findLayerById(prioritizeLayerId);
					parkServePlaceLayer.visible = true;

					view.whenLayerView(parkServePlaceLayer)?.then((layerView) => {
						layerView.filter = new FeatureFilter({
							where: `${placeSearchField} = '${name}'`,
						});
					});
					parkLayer.visible = true;
					parkServiceLayer.visible = true;
					priorityAreaPlaceFL.visible = true;
					view.whenLayerView(priorityAreaPlaceFL).then((layerView) => {
						layerView.filter = new FeatureFilter({
							where: `${dataFields["PlaceID"]} = '${attributes[dataFields["PlaceID"]]}' AND ${dataFields["Outside10MWOnly"]} = 1`,
						});
					});
				}
			}
		},
		[setCity, view]
	);

	const getUrban = useCallback(
		async (urbanID: number) => {
			const features = await urbanAreaLayer.queryFeatures({
				where: `${dataFields["UA_ID"]} = '${urbanID}'`,
				outFields: ["*"],
				returnGeometry: true,
				outSpatialReference: {
					wkid: 102100,
				},
			});
			if (features) {
				setUrban?.(features.features[0]);
			}
		},
		[setUrban, view]
	);

	return (
		<CityContext.Provider
			value={{
				city,
				setCity: getCity,
				urban,
				setUrban: getUrban,
				isInitialLoad,
				setInitialLoad,
			}}
			{...props}
		></CityContext.Provider>
	);
}
